import type { SketchToolType } from "../../../../../types/stage-types.js";

export interface PointLike {
  readonly x: number;
  readonly y: number;
}

export interface StrokeVisualStyle {
  readonly color: string;
  readonly width: number;
  readonly opacity?: number;
  readonly tool?: SketchToolType;
}

export interface StrokeRenderPayload extends StrokeVisualStyle {
  readonly id: string;
  readonly pathData: string;
}

export interface StrokeRendererAPI {
  readonly renderActiveStroke: (
    stroke: StrokeRenderPayload | null
  ) => void;
  readonly commitStroke: (stroke: StrokeRenderPayload) => void;
  readonly insertStroke: (
    stroke: StrokeRenderPayload,
    beforeId: string | null
  ) => void;
  readonly updateStroke: (stroke: StrokeRenderPayload) => void;
  readonly removeStroke: (strokeId: string) => void;
  readonly setEraserCursor: (
    payload: { readonly x: number; readonly y: number; readonly radius: number } | null
  ) => void;
  readonly reset: () => void;
  readonly destroy: () => void;
}

/**
 * Converts sketch-space points into SVG path data using a smoothed polyline.
 */
export const pointsToPath = (points: readonly PointLike[]): string => {
  const count = points.length;
  if (count === 0) {
    return "";
  }

  const start = points[0]!;
  let path = `M ${formatCoord(start.x)} ${formatCoord(start.y)}`;

  if (count === 1) {
    // Use a zero-length line so single taps render as dots.
    path += ` L ${formatCoord(start.x)} ${formatCoord(start.y)}`;
    return path;
  }

  if (count === 2) {
    const end = points[1]!;
    path += ` L ${formatCoord(end.x)} ${formatCoord(end.y)}`;
    return path;
  }

  for (let index = 1; index < count - 1; index += 1) {
    const current = points[index]!;
    const next = points[index + 1]!;
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;

    path += ` Q ${formatCoord(current.x)} ${formatCoord(
      current.y
    )} ${formatCoord(midX)} ${formatCoord(midY)}`;
  }

  const last = points[count - 1]!;
  path += ` L ${formatCoord(last.x)} ${formatCoord(last.y)}`;
  return path;
};

export const pointsToPolylinePath = (
  points: readonly PointLike[],
  closed: boolean
): string => {
  if (points.length === 0) return "";
  const start = points[0]!;
  let path = `M ${formatCoord(start.x)} ${formatCoord(start.y)}`;
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index]!;
    path += ` L ${formatCoord(point.x)} ${formatCoord(point.y)}`;
  }
  if (closed && points.length > 2) {
    path += " Z";
  }
  return path;
};

/**
 * Converts sketch-space points into a smooth SVG path using Catmull-Rom splines.
 */
export const pointsToSmoothPath = (points: readonly PointLike[]): string => {
  const count = points.length;
  if (count === 0) return "";

  const start = points[0]!;
  let path = `M ${formatCoord(start.x)} ${formatCoord(start.y)}`;

  if (count === 1) {
    path += ` L ${formatCoord(start.x)} ${formatCoord(start.y)}`;
    return path;
  }

  if (count === 2) {
    const end = points[1]!;
    path += ` L ${formatCoord(end.x)} ${formatCoord(end.y)}`;
    return path;
  }

  for (let index = 0; index < count - 1; index += 1) {
    const p0 = points[Math.max(0, index - 1)]!;
    const p1 = points[index]!;
    const p2 = points[index + 1]!;
    const p3 = points[Math.min(count - 1, index + 2)]!;

    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${formatCoord(c1x)} ${formatCoord(c1y)} ${formatCoord(
      c2x
    )} ${formatCoord(c2y)} ${formatCoord(p2.x)} ${formatCoord(p2.y)}`;
  }

  return path;
};

const formatCoord = (value: number): string =>
  Number.isFinite(value)
    ? String(Math.round(value * 100) / 100)
    : "0";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Imperative SVG stroke renderer that keeps DOM mutations scoped to the canvas layer.
 */
export const createStrokeRenderer = (
  root: SVGGElement,
  debug: boolean = false
): StrokeRendererAPI => {
  const highlighterGroup = document.createElementNS(SVG_NS, "g");
  highlighterGroup.classList.add(
    "canvas-layer__sketch-strokes",
    "canvas-layer__sketch-strokes--highlighter"
  );
  highlighterGroup.setAttribute("fill", "none");
  highlighterGroup.setAttribute("stroke-linecap", "round");
  highlighterGroup.setAttribute("stroke-linejoin", "round");
  highlighterGroup.setAttribute("pointer-events", "none");

  const committedGroup = document.createElementNS(SVG_NS, "g");
  committedGroup.classList.add("canvas-layer__sketch-strokes");
  committedGroup.setAttribute("fill", "none");
  committedGroup.setAttribute("stroke-linecap", "round");
  committedGroup.setAttribute("stroke-linejoin", "round");
  committedGroup.setAttribute("pointer-events", "none");

  const activeHighlighterPath = document.createElementNS(SVG_NS, "path");
  activeHighlighterPath.classList.add(
    "canvas-layer__sketch-stroke",
    "canvas-layer__sketch-stroke--active",
    "canvas-layer__sketch-stroke--highlighter"
  );
  activeHighlighterPath.setAttribute("fill", "none");
  activeHighlighterPath.setAttribute("pointer-events", "none");
  activeHighlighterPath.setAttribute("stroke-linecap", "round");
  activeHighlighterPath.setAttribute("stroke-linejoin", "round");

  const activePath = document.createElementNS(SVG_NS, "path");
  activePath.classList.add(
    "canvas-layer__sketch-stroke",
    "canvas-layer__sketch-stroke--active"
  );
  activePath.setAttribute("fill", "none");
  activePath.setAttribute("pointer-events", "none");
  activePath.setAttribute("stroke-linecap", "round");
  activePath.setAttribute("stroke-linejoin", "round");

  const eraserCursor = document.createElementNS(SVG_NS, "circle");
  eraserCursor.classList.add("canvas-layer__sketch-eraser");
  eraserCursor.setAttribute("pointer-events", "none");
  eraserCursor.setAttribute("fill", "none");
  eraserCursor.setAttribute("display", "none");

  root.appendChild(highlighterGroup);
  root.appendChild(committedGroup);
  root.appendChild(activeHighlighterPath);
  root.appendChild(activePath);
  root.appendChild(eraserCursor);

  const applyStyle = (
    element: SVGPathElement,
    style: StrokeVisualStyle
  ): void => {
    element.setAttribute("stroke", style.color);
    element.setAttribute("stroke-width", `${style.width}`);
    if (style.opacity !== undefined) {
      element.setAttribute("stroke-opacity", `${style.opacity}`);
    } else {
      element.removeAttribute("stroke-opacity");
    }
  };

  const renderActiveStroke = (
    stroke: StrokeRenderPayload | null
  ): void => {
    if (!stroke || !stroke.pathData) {
      activePath.removeAttribute("d");
      activeHighlighterPath.removeAttribute("d");
      return;
    }

    if (stroke.tool === "highlighter") {
      activePath.removeAttribute("d");
      activeHighlighterPath.setAttribute("d", stroke.pathData);
      applyStyle(activeHighlighterPath, stroke);
      return;
    }

    activeHighlighterPath.removeAttribute("d");
    activePath.setAttribute("d", stroke.pathData);
    applyStyle(activePath, stroke);
  };

  const createStrokePath = (stroke: StrokeRenderPayload): SVGPathElement => {
    const path = document.createElementNS(SVG_NS, "path");
    path.classList.add("canvas-layer__sketch-stroke");
    path.dataset.strokeId = stroke.id;
    path.setAttribute("d", stroke.pathData);
    path.setAttribute("fill", "none");
    path.setAttribute("pointer-events", "none");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    applyStyle(path, stroke);
    return path;
  };

  const commitStroke = (stroke: StrokeRenderPayload): void => {
    if (!stroke.pathData) {
      if (debug) {
        console.warn(
          `🚨 RENDERER: commitStroke SKIPPED - empty pathData for stroke ${stroke.id}`
        );
      }
      return;
    }

    const path = createStrokePath(stroke);
    const targetGroup =
      stroke.tool === "highlighter" ? highlighterGroup : committedGroup;
    targetGroup.appendChild(path);
    
  };

  const insertStroke = (
    stroke: StrokeRenderPayload,
    beforeId: string | null
  ): void => {
    if (!stroke.pathData) {
      if (debug) {
        console.warn(
          `🚨 RENDERER: insertStroke SKIPPED - empty pathData for stroke ${stroke.id}`
        );
      }
      return;
    }
    if (!beforeId) {
      commitStroke(stroke);
      return;
    }
    const targetGroup =
      stroke.tool === "highlighter" ? highlighterGroup : committedGroup;
    const escapeId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(beforeId)
        : beforeId.replace(/[^a-zA-Z0-9_-]/g, "");
    const beforeNode = targetGroup.querySelector(
      `[data-stroke-id="${escapeId}"]`
    );
    const path = createStrokePath(stroke);
    if (beforeNode) {
      targetGroup.insertBefore(path, beforeNode);
    } else {
      targetGroup.appendChild(path);
    }
  };

  const updateStroke = (stroke: StrokeRenderPayload): void => {
    if (!stroke.pathData) {
      if (debug) {
        console.warn(
          `🚨 RENDERER: updateStroke SKIPPED - empty pathData for stroke ${stroke.id}`
        );
      }
      return;
    }
    const escapeId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(stroke.id)
        : stroke.id.replace(/[^a-zA-Z0-9_-]/g, "");
    const path = root.querySelector(
      `[data-stroke-id="${escapeId}"]`
    ) as SVGPathElement | null;
    if (!path) {
      commitStroke(stroke);
      return;
    }
    path.setAttribute("d", stroke.pathData);
    applyStyle(path, stroke);
  };

  const removeStroke = (strokeId: string): void => {
    const escapeId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(strokeId)
        : strokeId.replace(/[^a-zA-Z0-9_-]/g, "");
    const path = root.querySelector(
      `[data-stroke-id="${escapeId}"]`
    ) as SVGPathElement | null;
    if (path && path.parentElement) {
      path.parentElement.removeChild(path);
    }
  };

  const setEraserCursor = (
    payload: { readonly x: number; readonly y: number; readonly radius: number } | null
  ): void => {
    if (!payload) {
      eraserCursor.setAttribute("display", "none");
      return;
    }
    eraserCursor.setAttribute("display", "");
    eraserCursor.setAttribute("cx", `${payload.x}`);
    eraserCursor.setAttribute("cy", `${payload.y}`);
    eraserCursor.setAttribute("r", `${Math.max(1, payload.radius)}`);
  };

  const reset = (): void => {
    while (highlighterGroup.firstChild) {
      highlighterGroup.removeChild(highlighterGroup.firstChild);
    }
    while (committedGroup.firstChild) {
      committedGroup.removeChild(committedGroup.firstChild);
    }
    activePath.removeAttribute("d");
    activeHighlighterPath.removeAttribute("d");
    eraserCursor.setAttribute("display", "none");
  };

  const destroy = (): void => {
    reset();
    if (activePath.parentNode === root) {
      root.removeChild(activePath);
    }
    if (activeHighlighterPath.parentNode === root) {
      root.removeChild(activeHighlighterPath);
    }
    if (committedGroup.parentNode === root) {
      root.removeChild(committedGroup);
    }
    if (highlighterGroup.parentNode === root) {
      root.removeChild(highlighterGroup);
    }
    if (eraserCursor.parentNode === root) {
      root.removeChild(eraserCursor);
    }
  };

  return {
    renderActiveStroke,
    commitStroke,
    insertStroke,
    updateStroke,
    removeStroke,
    setEraserCursor,
    reset,
    destroy,
  };
};
